import React from 'react';

export interface MicrosoftLogoProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 20). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Microsoft four-square brand logo, as a reusable SVG React component.
 *
 * The four colours are defined by Microsoft's brand guidelines and MUST NOT be
 * altered — only the surrounding button/background may be themed. The logo is
 * decorative by default (aria-hidden) since it is normally paired with a text
 * label such as 'Sign in with Microsoft'.
 *
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the Microsoft logo SVG element.
 */
function MicrosoftLogo({ size = 20, className = '', title, ...rest }: MicrosoftLogoProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 21 21"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Pure presentational icon — memoised so it never re-renders unless its props
 * change (useful if reused inside lists/tables later).
 */
export default React.memo(MicrosoftLogo);
